/**
 * ICE score service
 *
 * Calls ideoz-ai-api directly via aiApi (same bearer token as everything else,
 * no SSE). Source: docs/Integration/ICE-Scores-Frontend-Guide.md.
 *
 * @module @ideoz/core/api/services/ice.service
 */

import apiClient from '../client';
import { ApiUrls } from '../ApiUrls';
import type {
  IceEvaluateConflictResponse,
  IceEvaluateQueuedResponse,
  IceEvaluateResult,
  IceRankingResponse,
  IceScopeResponse,
} from '../../types/ice.types';

/** Founder-safe fallback for the `503` body when the backend sends no copy. */
const QUEUE_DOWN_MESSAGE = 'The evaluation could not be queued. Try again shortly.';

const statusOf = (error: any): number | undefined => error?.response?.status;

export const iceService = {
  /**
   * Queue an evaluation of a scope.
   *
   * No request body — the backend derives the trigger, so whether the founder
   * sees a change diff never depends on which client made the call.
   *
   * `409` is *not* an error: it carries the already-running evaluation's id,
   * which the caller adopts and polls. `503` is definitive — nothing will run,
   * so the caller must not start polling. `404`/`500` throw.
   */
  async evaluateScope(projectId: string, scopeId: string): Promise<IceEvaluateResult> {
    try {
      const response = await apiClient.post<IceEvaluateQueuedResponse>(
        ApiUrls.evaluateScopeIce(projectId, scopeId)
      );
      return { outcome: 'queued', data: response.data };
    } catch (error: any) {
      const status = statusOf(error);

      if (status === 409) {
        const data = error.response?.data as IceEvaluateConflictResponse | undefined;
        if (data?.evaluation_id) {
          return { outcome: 'already_running', data };
        }
      }

      if (status === 503) {
        // `message` is founder-safe copy; `error` on the same body is not.
        const message = error.response?.data?.message;
        return {
          outcome: 'queue_unavailable',
          message: typeof message === 'string' && message ? message : QUEUE_DOWN_MESSAGE,
        };
      }

      throw error;
    }
  },

  /**
   * Read a scope's current score, diff, card, and any run in flight.
   *
   * Reading never computes, so this is safe to poll. `404` means the scope has
   * never been evaluated and nothing is running — returned as `null` rather
   * than thrown, since it is an ordinary empty state.
   */
  async getScopeIce(projectId: string, scopeId: string): Promise<IceScopeResponse | null> {
    try {
      const response = await apiClient.get<IceScopeResponse>(
        ApiUrls.getScopeIce(projectId, scopeId)
      );
      return response.data;
    } catch (error: any) {
      if (statusOf(error) === 404) return null;
      throw error;
    }
  },

  /** Read the project's scopes ranked by ICE. */
  async getProjectRanking(projectId: string): Promise<IceRankingResponse> {
    const response = await apiClient.get<IceRankingResponse>(
      ApiUrls.getProjectIceRanking(projectId)
    );
    return response.data;
  },
};

export default iceService;
